import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
  Backdrop,
  LinearProgress,
  Divider,
} from "@mui/material";
import { CalendarDays, Clock, Package, X, Truck } from "lucide-react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  deleteMoveService,
  getMoveService,
  sendCancellationNotification,
  updateMoveService,
} from "../../redux/action/move-service";
import CancelMoveModal from "../../modals/CancelMoveModal";

import ChangeDeliveryModal from "../../modals/ChangeDeliveryModal";
import ChangePickupModal from "../../modals/ChangePickupModal";
import ChangeInventoryModal from "../../modals/ChangeInventoryModal";
import EditMoveDateModal from "../../modals/EditMoveDateModal";

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

interface MoveScheduleProps {
  selectedMoveServiceData: any;
  refreshMoveData: any;
}

const MoveSchedule = ({
  selectedMoveServiceData,
  refreshMoveData,
}: MoveScheduleProps) => {
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const cancellationPeriod =
    selectedMoveServiceData?.carrier?.cancellation_period;
  const handleDateUpdate = async () => {
    setIsLoading(true);
    setLoadingMessage("Updating move date...");
    await refreshMoveData(); // Wait for the data to refresh
    setIsLoading(false);
    setIsDateModalOpen(false);
  };

  const handleCancelMove = async (reason: string, details?: string) => {
    setIsLoading(true);
    setLoadingMessage("Processing cancellation...");
    try {
      const cancellationDetails = reason === "Other" ? details : reason;
      const cancelNotificationData = {
        moveId: selectedMoveServiceData.id,
        reason: cancellationDetails,
      };
      await dispatch<any>(sendCancellationNotification(cancelNotificationData))
        .then(unwrapResult)
        .then((res: any) => {});

      const updateData = {
        canonical_id: selectedMoveServiceData.canonical_id,
        status: "cancelled",
      };

      await dispatch<any>(updateMoveService(updateData))
        .then(unwrapResult)
        .then(() => {
          refreshMoveData();
          setIsCancelModalOpen(false);
        });
    } catch (error) {
      console.error("Failed to cancel move:", error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Box sx={{ width: isMobile ? "100%" : "70%", paddingTop: "20px" }}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: "absolute",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        open={isLoading}
      >
        <Box sx={{ width: "80%", maxWidth: 400 }}>
          <LinearProgress />
          <Typography
            fontSize={16}
            fontWeight={500}
            textAlign="center"
            mt={2}
            color="white"
          >
            {loadingMessage}
          </Typography>
        </Box>
      </Backdrop>
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
            Move Schedule
          </Typography>

          {/* <Box
            sx={{ backgroundColor: "#f9fafb", p: 4, borderRadius: 2, mb: 6 }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              <Box sx={{ flex: "1 1 calc(50% - 16px)" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Moving Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {formatDate(selectedMoveServiceData?.moving_date)}
                </Typography>
              </Box>
              <Box sx={{ flex: "1 1 calc(50% - 16px)" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Pickup Details
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {selectedMoveServiceData?.dwelling_type}
                </Typography>
              </Box>
              <Box sx={{ flex: "1 1 calc(50% - 16px)" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Delivery Window
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                  {selectedMoveServiceData?.deliveryDetails?.dwelling_type}
                </Typography>
              </Box>
              <Box sx={{ flex: "1 1 calc(50% - 16px)" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  No-Cancel Date
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "medium", color: "red" }}
                >
                  {formatDate(selectedMoveServiceData?.noCancelDate)}
                </Typography>
              </Box>
            </Box>
          </Box> */}
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {/* Modify Date Button */}

            {/*  <Box sx={{ flex: "1 1 calc(33.33% - 16px)" }}>
              <Button
                onClick={() => setIsPickupModalOpen(true)}
                variant="outlined"
                fullWidth
                sx={{
                  height: 45,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                <Clock style={{ marginRight: 8 }} />
                Change Pickup
              </Button>
            </Box>

            <Box sx={{ flex: "1 1 calc(33.33% - 16px)" }}>
              <Button
                onClick={() => setIsDeliveryModalOpen(true)} // Open modal on click
                variant="outlined"
                fullWidth
                sx={{
                  height: 45,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                <Truck style={{ marginRight: 8 }} />
                Change Delivery
              </Button>
            </Box>

            <Box sx={{ flex: "1 1 calc(33.33% - 16px)" }}>
              <Button
                onClick={() => setIsInventoryModalOpen(true)}
                variant="outlined"
                fullWidth
                sx={{
                  height: 45,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                <Package style={{ marginRight: 8 }} />
                Modify Inventory
              </Button>
            </Box> */}
            <Box sx={{ flex: "1 1 calc(33.33% - 16px)" }}>
              <Button
                onClick={() => setIsDateModalOpen(true)}
                variant="outlined"
                fullWidth
                sx={{
                  height: 45,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                <CalendarDays style={{ marginRight: 8 }} />
                Modify Date
              </Button>
            </Box>

            {/* Cancel Move Button */}
            <Box sx={{ flex: "1 1 calc(33.33% - 16px)" }}>
              <Button
                onClick={() => setIsCancelModalOpen(true)}
                variant="contained"
                fullWidth
                disabled={selectedMoveServiceData?.status === "cancelled"}
                sx={{
                  height: 45,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textTransform: "none",
                  fontWeight: "bold",
                  backgroundColor:
                    selectedMoveServiceData?.status === "cancelled"
                      ? "grey"
                      : "red",
                  "&:hover": {
                    backgroundColor:
                      selectedMoveServiceData?.status === "cancelled"
                        ? "grey"
                        : "darkred",
                  },
                }}
              >
                <X style={{ marginRight: 8 }} />
                {selectedMoveServiceData?.status === "cancelled"
                  ? "Move Cancelled"
                  : "Cancel Move"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <EditMoveDateModal
        open={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        currentDate={selectedMoveServiceData?.moving_date}
        canonicalId={selectedMoveServiceData?.canonical_id}
        onUpdateSuccess={handleDateUpdate}
        cancellationPeriod={cancellationPeriod}
      />
      <CancelMoveModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onCancel={handleCancelMove}
        cancellationPeriod={cancellationPeriod}
        currentDate={selectedMoveServiceData?.moving_date}
      />
      <ChangeDeliveryModal
        open={isDeliveryModalOpen}
        onClose={() => setIsDeliveryModalOpen(false)}
        canonicalId={selectedMoveServiceData?.canonical_id}
      />
      <ChangePickupModal
        open={isPickupModalOpen}
        onClose={() => setIsPickupModalOpen(false)}
        canonicalId={selectedMoveServiceData?.canonical_id}
        selectedMoveServiceData={selectedMoveServiceData}
      />
      <ChangeInventoryModal
        open={isInventoryModalOpen}
        onClose={() => setIsInventoryModalOpen(false)}
        getMoveService={selectedMoveServiceData}
      />
    </Box>
  );
};

export default MoveSchedule;
